import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, Users, Globe, Inbox, SlidersHorizontal, LifeBuoy, MessageSquare, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MARKETING_URL } from "@/lib/appLink";

const navItems = [
  { to: "/", label: "Dashboard", icon: LayoutGrid, testId: "nav-dashboard" },
  { to: "/clients", label: "Clients", icon: Users, testId: "nav-clients" },
  { to: "/sites", label: "Sites", icon: Globe, testId: "nav-sites" },
  { to: "/audits", label: "Audits", icon: Inbox, testId: "nav-audits" },
  { to: "/settings", label: "Settings", icon: SlidersHorizontal, testId: "nav-settings" },
];

const supportItems = [
  // The help centre is public and indexable, so it lives on the marketing site
  // and opens in a new tab: following it should not throw away whatever the
  // person was in the middle of doing in the dashboard.
  { href: MARKETING_URL + "/help", label: "Help center", icon: LifeBuoy, testId: "nav-help" },
  { to: "/feedback", label: "Feedback", icon: MessageSquare, testId: "nav-feedback" },
];

const itemCls = (isActive) =>
  `flex items-center gap-2.5 px-5 py-2.5 text-sm border-l-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-inset ${
    isActive
      ? "border-[#16324F] bg-[#F1F4F7] text-[#16324F] font-medium"
      : "border-transparent text-ink-muted hover:text-ink hover:bg-gray-50"
  }`;

const NavItem = ({ to, href, label, icon: Icon, testId }) => (
  <li>
    {href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={itemCls(false)}
      >
        <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
        {label}
        <ExternalLink size={13} strokeWidth={1.5} aria-hidden="true" className="ml-auto text-ink-subtle" />
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    ) : (
      <NavLink to={to} end={to === "/"} data-testid={testId} className={({ isActive }) => itemCls(isActive)}>
        <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
        {label}
      </NavLink>
    )}
  </li>
);

export const Layout = ({ children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-ink">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:bg-white focus:border focus:border-[#16324F] focus:px-3 focus:py-2 rounded-sm">
        Skip to main content
      </a>
      <div className="flex min-h-screen">
        {/* Sticky full-height rail: the page scrolls, the sidebar stays put. */}
        <aside className="w-60 shrink-0 border-r border-gray-200 bg-white flex flex-col sticky top-0 h-screen" aria-label="Primary">
          <div className="px-5 py-5 border-b border-gray-200">
            <img
              src="/Complaudax_logo.svg"
              alt="Complaudax"
              data-testid="app-logo"
              className="block h-7 w-auto max-w-full"
            />
            <p className="text-xs text-ink-muted font-mono mt-1.5">WCAG Conformance ledger</p>
          </div>
          <nav className="flex-1 min-h-0 overflow-y-auto py-3 flex flex-col" aria-label="Main navigation">
            <ul>
              {navItems.map((item) => <NavItem key={item.testId} {...item} />)}
            </ul>
            <ul className="mt-auto border-t border-gray-200 pt-3" aria-label="Support">
              {supportItems.map((item) => <NavItem key={item.testId} {...item} />)}
            </ul>
          </nav>
          <div className="border-t border-gray-200 px-5 py-4">
            <p className="text-xs font-mono text-ink-muted truncate mb-2" data-testid="user-email">{user?.email}</p>
            <button
              data-testid="sign-out-btn"
              onClick={async () => { await signOut(); navigate("/login"); }}
              className="inline-flex items-center gap-2 text-sm text-ink-muted bg-transparent border border-gray-300 rounded-sm px-3 py-1.5 hover:text-[#C62828] hover:border-[#C62828] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#16324F] focus-visible:ring-offset-2"
            >
              <LogOut size={14} strokeWidth={1.5} aria-hidden="true" /> Sign out
            </button>
          </div>
        </aside>
        <main id="main-content" className="flex-1 min-w-0 px-8 py-8">{children}</main>
      </div>
    </div>
  );
};
